import { useEffect, useState } from "react";
import api from "../../lib/api";

const EMPTY_ITEM = {
    part_id: "", description: "", sku: "", unit: "pc", qty: 1, unit_price: 0, discount_pct: 0, note: "",
};

/**
 * Editable list of line items. Optionally lets the user pick from the parts
 * master (SKU auto-fills description + unit + price when selected). Emits
 * `onChange(items)` on every mutation. Read-only when `readonly` is true.
 */
export default function LineItemsEditor({ items, onChange, currency = "AED", vatRate = 5, readonly = false }) {
    const [parts, setParts] = useState([]);

    useEffect(() => {
        if (readonly) return;
        api.get("/parts?active=true&limit=500")
            .then(({ data }) => setParts(data.items || []))
            .catch(() => setParts([]));
    }, [readonly]);

    const setItem = (idx, patch) => {
        const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
        onChange(next);
    };

    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

    const add = () => onChange([...items, { ...EMPTY_ITEM }]);

    const pickPart = (idx, partId) => {
        if (!partId) {
            setItem(idx, { part_id: "", sku: "" });
            return;
        }
        const p = parts.find((x) => x.id === partId);
        if (!p) return;
        setItem(idx, {
            part_id: p.id,
            sku: p.sku,
            unit: p.unit,
            unit_price: +p.unit_price,
            description: items[idx].description || p.name,
        });
    };

    // Totals preview (client-side, backend is authoritative)
    let subtotal = 0, discount = 0;
    for (const it of items) {
        const q = +it.qty || 0; const p = +it.unit_price || 0; const d = +it.discount_pct || 0;
        const gross = q * p;
        subtotal += gross;
        discount += gross * d / 100;
    }
    const taxable = Math.max(0, subtotal - discount);
    const vat = taxable * vatRate / 100;
    const total = taxable + vat;

    return (
        <div className="space-y-3" data-testid="line-items-editor">
            <div className="lx-label text-white/50">Line items</div>
            {items.length === 0 && (
                <div className="text-xs text-white/40 py-3">No items yet. Add one below.</div>
            )}
            {items.map((it, idx) => (
                <div key={idx} data-testid={`line-item-${idx}`} className="border border-[#2B1438] p-3 space-y-2 bg-[#0F0614]/40">
                    <div className="grid grid-cols-12 gap-2">
                        <select
                            data-testid={`li-part-${idx}`}
                            value={it.part_id || ""}
                            onChange={(e) => pickPart(idx, e.target.value)}
                            disabled={readonly}
                            className="lx-in col-span-3 text-xs"
                        >
                            <option value="">— Free line —</option>
                            {parts.map((p) => (
                                <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>
                            ))}
                        </select>
                        <input
                            data-testid={`li-desc-${idx}`}
                            value={it.description}
                            onChange={(e) => setItem(idx, { description: e.target.value })}
                            placeholder="Description"
                            disabled={readonly}
                            className="lx-in col-span-5 text-xs"
                        />
                        <input
                            data-testid={`li-qty-${idx}`}
                            type="number" min="0" step="0.01"
                            value={it.qty}
                            onChange={(e) => setItem(idx, { qty: e.target.value })}
                            placeholder="Qty"
                            disabled={readonly}
                            className="lx-in col-span-1 text-xs"
                        />
                        <input
                            data-testid={`li-price-${idx}`}
                            type="number" min="0" step="0.01"
                            value={it.unit_price}
                            onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                            placeholder="Unit"
                            disabled={readonly}
                            className="lx-in col-span-2 text-xs"
                        />
                        <input
                            data-testid={`li-disc-${idx}`}
                            type="number" min="0" max="100" step="1"
                            value={it.discount_pct}
                            onChange={(e) => setItem(idx, { discount_pct: e.target.value })}
                            placeholder="%"
                            disabled={readonly}
                            className="lx-in col-span-1 text-xs"
                            title="Discount %"
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <div className="text-[10px] text-white/40">
                            {it.sku && <span className="mr-3">SKU: <span className="text-[#D4AF37] font-mono">{it.sku}</span></span>}
                            Line: <span className="text-white">{currency} {((+it.qty || 0) * (+it.unit_price || 0) * (1 - (+it.discount_pct || 0) / 100)).toFixed(2)}</span>
                        </div>
                        {!readonly && (
                            <button
                                type="button"
                                data-testid={`li-remove-${idx}`}
                                onClick={() => remove(idx)}
                                className="text-[10px] text-rose-400 hover:text-rose-300 uppercase tracking-wider"
                            >
                                Remove
                            </button>
                        )}
                    </div>
                </div>
            ))}
            {!readonly && (
                <button
                    type="button"
                    data-testid="line-item-add"
                    onClick={add}
                    className="text-xs uppercase tracking-wider text-[#D4AF37] hover:underline"
                >
                    + Add line item
                </button>
            )}

            <div className="border-t border-[#2B1438] pt-3 mt-2 space-y-1 text-xs">
                <Row label="Subtotal" value={`${currency} ${subtotal.toFixed(2)}`} />
                <Row label="Discount" value={`- ${currency} ${discount.toFixed(2)}`} />
                <Row label={`VAT (${vatRate}%)`} value={`${currency} ${vat.toFixed(2)}`} />
                <div className="flex justify-between pt-2 border-t border-[#2B1438]">
                    <span className="lx-label text-[#D4AF37]">Total</span>
                    <span className="font-serif text-xl text-white">{currency} {total.toFixed(2)}</span>
                </div>
            </div>
            <style>{`.lx-in{padding:0.5rem 0.6rem;background:#0F0614;border:1px solid #2B1438;color:white;outline:none;width:100%}.lx-in:focus{border-color:#D4AF37}.lx-in:disabled{opacity:0.6}`}</style>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between">
            <span className="text-white/50">{label}</span>
            <span className="text-white tabular-nums">{value}</span>
        </div>
    );
}
